/**
 * @file     gatedFetch.ts
 * @purpose  Browser half of the phone gate. gatedSecureFetch posts like
 *           secureFetch; when the route holds the request for a code (202
 *           `pending: 'phone_code'`), it opens the site-wide code dialog
 *           (PhoneGateHost) and, once the code is accepted, resubmits the same
 *           request with the proof. Callers see one response, as before.
 */
'use client';

import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { PHONE_GATE_TTL_MS } from './phoneGateTtl';
import { toE164 } from '@/utils/formatPhone';

export interface PhoneGatePrompt {
  phone: string;
  kind: string;
  channel: 'sms' | 'call';
  /**
   * `code`: a code was sent, enter it. `edit`: the number was refused, correct
   * it first. `blocked`: too many codes, nothing to enter (close only).
   */
  mode?: 'code' | 'edit' | 'blocked';
}

/** The proof, plus the number it is for (the person may have corrected it). */
export interface PhoneGateResult {
  token: string;
  phone: string;
}

type Listener = (prompt: PhoneGatePrompt, resolve: (result: PhoneGateResult | null) => void) => void;

let listener: Listener | null = null;

/** PhoneGateHost registers itself; one host for the whole site. */
export function registerPhoneGateHost(fn: Listener): () => void {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

/** Resolves with the proof, or null when the person closes the dialog. */
export function promptPhoneCode(prompt: PhoneGatePrompt): Promise<PhoneGateResult | null> {
  const host = listener;
  if (!host) return Promise.resolve(null);
  return new Promise((resolve) => host(prompt, resolve));
}

/**
 * Proofs earned in this tab, by number. The server accepts a proof for any form
 * (it attests the number, not the form), so a second form for the same number
 * resubmits with the cached proof instead of asking again.
 */
const proofs = new Map<string, { token: string; expires: number }>();

function cachedProof(phone: string): string | null {
  const hit = proofs.get(phone);
  if (!hit) return null;
  if (hit.expires < Date.now()) {
    proofs.delete(phone);
    return null;
  }
  return hit.token;
}

function rememberProof(phone: string, token: string): void {
  // Shorter than the server window so a cached proof is never the one that lapses mid-request.
  proofs.set(phone, { token, expires: Date.now() + PHONE_GATE_TTL_MS - 60_000 });
}

/** SMS consent was given for the number the person typed; a corrected number has none. */
const SMS_CONSENT_KEYS = ['smsOptIn', 'smsConsent', 'smsConsentVersion', 'smsConsentAt'];

/** Swap `from` for `to` wherever a `phone` field carries it, so a corrected number reaches the route. */
function swapPhone(value: unknown, from: string, to: string): unknown {
  if (Array.isArray(value)) return value.map((v) => swapPhone(v, from, to));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) =>
        k === 'phone' && typeof v === 'string' && toE164(v) === from ? [k, to] : [k, swapPhone(v, from, to)],
      ),
    );
  }
  return value;
}

function withToken(body: BodyInit | null | undefined, token: string, swap?: { from: string; to: string }): BodyInit | null | undefined {
  if (typeof body === 'string') {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    const next = swap ? (swapPhone(parsed, swap.from, swap.to) as Record<string, unknown>) : parsed;
    if (swap) for (const key of SMS_CONSENT_KEYS) delete next[key];
    return JSON.stringify({ ...next, phoneGateToken: token });
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    if (swap && toE164(String(body.get('phone') ?? '')) === swap.from) {
      body.set('phone', swap.to);
      for (const key of SMS_CONSENT_KEYS) body.delete(key);
    }
    body.set('phoneGateToken', token);
    return body;
  }
  return body;
}

export const PHONE_UNCONFIRMED = 'phone_unconfirmed';

type GateJson = { pending?: string; phone?: string; kind?: string; channel?: 'sms' | 'call'; error?: string };

async function readJson(res: Response): Promise<GateJson | null> {
  try {
    return (await res.clone().json()) as GateJson;
  } catch {
    return null;
  }
}

const unconfirmed = () =>
  new Response(JSON.stringify({ success: false, error: PHONE_UNCONFIRMED, message: 'Confirm your number to send this.' }), {
    status: 428,
    headers: { 'content-type': 'application/json' },
  });

/** Why a gated request failed at the phone step, or null when it failed for another reason. */
export async function phoneGateFailure(res: Response): Promise<'unconfirmed' | 'rate_limited' | 'invalid_phone' | null> {
  if (res.status === 428) return 'unconfirmed';
  const json = await readJson(res);
  if (res.status === 429 && json?.error === 'phone_code_rate_limited') return 'rate_limited';
  if (res.status === 400 && json?.error === 'invalid_phone' && json.phone) return 'invalid_phone';
  return null;
}

async function resubmit(url: string, init: RequestInit, result: PhoneGateResult, from: string): Promise<Response> {
  rememberProof(result.phone, result.token);
  const swap = result.phone !== from ? { from, to: result.phone } : undefined;
  return secureFetch(url, { ...init, body: withToken(init.body, result.token, swap) });
}

export async function gatedSecureFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const first = await secureFetch(url, init);

  if (first.status === 400 || first.status === 429) {
    const failure = await phoneGateFailure(first);
    if (!failure) return first;
    const body = await readJson(first);
    const phone = body?.phone ?? '';
    if (failure === 'invalid_phone') {
      // The number cannot receive a code: let the person correct it in place.
      const result = await promptPhoneCode({ phone, kind: '', channel: 'sms', mode: 'edit' });
      if (!result) return unconfirmed();
      return resubmit(url, init, result, phone);
    }
    await promptPhoneCode({ phone, kind: '', channel: 'sms', mode: 'blocked' });
    return first;
  }

  if (first.status !== 202) return first;
  const json = await readJson(first);
  if (json?.pending !== 'phone_code' || !json.phone || !json.kind) return first;

  const cached = cachedProof(json.phone);
  if (cached) {
    const again = await secureFetch(url, { ...init, body: withToken(init.body, cached) });
    if (again.status !== 202) return again;
    proofs.delete(json.phone);
  }

  const result = await promptPhoneCode({ phone: json.phone, kind: json.kind, channel: json.channel ?? 'sms', mode: 'code' });
  if (!result) return unconfirmed();
  return resubmit(url, init, result, json.phone);
}
